import {
  DefaultFullPPGDataSchemaResponse,
  DefaultPPGDataSchemaResponse,
  PermissionBit,
  SignalGaps,
} from "../../../api";
import { AccessRules } from "../../../constants/roles";
import { DataModel } from "../../../models/data-model";
import { Merge } from "../../../utils/types";

export type PPGsItem = Merge<
  DefaultFullPPGDataSchemaResponse | DefaultPPGDataSchemaResponse
> & {
  deviceBattery?: number;
  statusPercentage?: Record<string, unknown>;
  /** Samsung watches report gaps per wavelength. */
  signalGaps?: SignalGaps | Record<string, SignalGaps>;
  gappy?: boolean;
};

export class PPGs extends DataModel<PPGsItem> {
  constructor(
    data: PPGsItem[],
    permissions: PermissionBit[],
    userUUID: string,
  ) {
    super(data, permissions, userUUID);

    // access
    this.access = {
      editRules: {
        medical: AccessRules.visible,
        monitoring: AccessRules.visible,
      },
      deleteRules: { medical: AccessRules.none, monitoring: AccessRules.none },
      historyRequiresMonitoring: false,
    };
  }

  updateData(data: PPGsItem[]) {
    super.updateData(
      data.map((item) => {
        const deviceStatus = item.measurement_device_status;
        const deviceBattery =
          deviceStatus && "battery" in deviceStatus
            ? deviceStatus.battery
            : undefined;
        const statusCounts = !deviceStatus
          ? undefined
          : "pkg_status_stats" in deviceStatus
            ? deviceStatus.pkg_status_stats
            : "state_stats" in deviceStatus
              ? deviceStatus.state_stats
              : undefined;
        const signalGaps = deviceStatus?.signal_gaps ?? undefined;
        return {
          ...item,
          ["deviceBattery"]: deviceBattery,
          ["statusPercentage"]: countsToPercentages(statusCounts),
          signalGaps,
          gappy: signalGaps && isGappy(signalGaps),
        };
      }),
    );
  }
}

function countsToPercentages(counts?: Record<string, number>) {
  if (counts === null || counts === undefined) {
    return counts;
  }
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return Object.fromEntries(
    Object.entries(counts).map(([k, v]) => [
      k,
      `${Math.round((v / total) * 100)}%`,
    ]),
  );
}

function isGappy(gaps: SignalGaps | Record<string, SignalGaps>): boolean {
  return "gappy" in gaps
    ? (gaps as SignalGaps).gappy
    : Object.values(gaps).some((g) => g.gappy);
}
